import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../config/supabase';
import { dataService } from '../services/dataService';
import { formatCurrency, getUserCurrency, getCurrencyByLocation, formatCurrencySync } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import BottomTabNav from '../shared/components/BottomTabNav';
import './CustomerProfile.css';

const CustomerProfile = () => {
  const { user, signOut, ensureCustomerRecord } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ orders: 0, spent: 0, rating: null });
  const [loadingStats, setLoadingStats] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached, loading: currencyLoading } = useCurrencyCache();


  useEffect(() => {
    // Ensure customer record exists when component loads
    if (user) {
      ensureCustomerRecord(user);
      loadCustomerInfo();
    }
  }, [user, ensureCustomerRecord]);

  const loadCustomerInfo = async () => {
    if (!user?.id && !user?.email) return;
    try {
      // Prefer lookup by auth UID; fallback to email
      let customerRecord = await dataService.getCustomerByAuthUid(user.id).catch(() => null);
      if (!customerRecord && user?.email) {
        customerRecord = await dataService.getCustomerByEmail(user.email).catch(() => null);
      }
      if (customerRecord) {
        const correctedCurrency = customerRecord.currency || await getCurrencyByLocation(customerRecord.country, customerRecord.city);
        setCustomerInfo({ ...customerRecord, currency: correctedCurrency });
      }
    } catch (error) {
      console.error('Error loading customer info:', error);
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      if (!user?.id && !user?.email) return;
      try {
        setLoadingStats(true);
        // Resolve current customer id by auth UID first, then email
        let customerId = null;
        let record = await dataService.getCustomerByAuthUid(user.id).catch(() => null);
        if (!record && user?.email) {
          record = await dataService.getCustomerByEmail(user.email).catch(() => null);
        }
        customerId = record?.id;
        if (!customerId) return;

        const orders = await dataService.getOrdersByCustomer(customerId).catch(() => []);
        const ordersCount = Array.isArray(orders) ? orders.length : 0;
        const totalSpent = (Array.isArray(orders) ? orders : []).reduce((sum, o) => {
          const totalField = o?.total ?? o?.final_total ?? 0;
          const numeric = typeof totalField === 'number' ? totalField : parseFloat(totalField) || 0;
          return sum + numeric;
        }, 0);
        const ratings = (Array.isArray(orders) ? orders : [])
          .map(o => {
            const possible = [o?.customer_rating, o?.rating, o?.customer_review_rating];
            return possible.find(v => typeof v === 'number' && !Number.isNaN(v));
          })
          .filter(v => typeof v === 'number');
        const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length) : null;

        setStats({ orders: ordersCount, spent: totalSpent, rating: avgRating });
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleContactUs = () => {
    navigate('/support');
  };

  const handleMyOrders = () => {
    // Navigate to orders page
    navigate('/orders');
  };


  return (
    <div className="App">
      <div className="profile-container">
        {/* Hero Section */}
        <div className="profile-hero">
          <div className="hero-background"></div>
          <div className="hero-content">
            <div className="profile-avatar-large">
              <div className="vector-avatar">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="60" fill="url(#avatarGradient)"/>
                  <text 
                    x="60" 
                    y="70" 
                    textAnchor="middle" 
                    fontSize="36" 
                    fontWeight="700" 
                    fill="white"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {user?.user_metadata?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </text>
                  <defs>
                    <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#667eea"/>
                      <stop offset="100%" stopColor="#764ba2"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="avatar-status"></div>
            </div>
            <h1 className="profile-name-hero">{user?.user_metadata?.name || 'User'}</h1>
            <p className="profile-email-hero">{user?.email}</p>
            {customerInfo && (
              <div className="profile-location-info" style={{
                marginTop: '10px',
                padding: '8px 16px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>📍</span>
                <span>
                  {customerInfo.city && customerInfo.country 
                    ? `${customerInfo.city}, ${customerInfo.country}`
                    : customerInfo.country || 'Location not set'
                  }
                </span>
                <span style={{ margin: '0 4px' }}>•</span>
                <span>
                  {customerInfo.currency || 'PKR'}
                </span>
              </div>
            )}
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-number">{stats.orders}</span>
                <span className="stat-label">Orders</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">{stats.rating ? stats.rating.toFixed(1) : '-'}</span>
                <span className="stat-label">Rating</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">{formatCurrencyCached(stats.spent, customerInfo?.currency || getUserCurrency())}</span>
                {/* Debug: Check currency values */}
                {(() => {
                  console.log('Profile Debug Currency:', {
                    statsSpent: stats.spent,
                    customerCurrency: customerInfo?.currency,
                    userCurrency: getUserCurrency(),
                    finalCurrency: customerInfo?.currency || getUserCurrency(),
                    currencyLoading: currencyLoading
                  });
                  return null;
                })()}
                <span className="stat-label">Spent</span>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-content">
          {/* Quick Actions */}
          <div className="quick-actions">
            <div className="action-card" onClick={handleMyOrders}>
              <div className="action-icon orders">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-text">My Orders</span>
            </div>
            
            <div className="action-card" onClick={handleEditProfile}>
              <div className="action-icon edit">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-text">Edit Profile</span>
            </div>
            
            <div className="action-card" onClick={handleContactUs}>
              <div className="action-icon support">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.9706 16.9706 21 12 21C10.4001 21 8.88837 20.6243 7.54753 19.9565L3 21L4.04346 16.4525C3.37568 15.1116 3 13.5999 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="action-text">Support</span>
            </div>
            
            <div className="action-card" onClick={handleLogout}>
              <div className="action-icon logout">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9M16 17L21 12L16 7M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
              <span className="action-text">Logout</span>
            </div>
          </div>

          {/* Menu Options */}
          <div className="menu-options">
            <div className="menu-section">
              <h3 className="menu-section-title">Account</h3>
              <Link to="/personal-info" className="menu-item">
                <div className="menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="menu-content">
                  <span className="menu-title">Personal Information</span>
                  <span className="menu-subtitle">Update your profile details</span>
                </div>
                <div className="menu-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>

              <div className="menu-item" onClick={handleMyOrders}>
                <div className="menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15M9 5C9 5.53043 9.21071 6.03914 9.58579 6.41421C9.96086 6.78929 10.4696 7 11 7H13C13.5304 7 14.0391 6.78929 14.4142 6.41421C14.7893 6.03914 15 5.53043 15 5M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="menu-content">
                  <span className="menu-title">Order History</span>
                  <span className="menu-subtitle">Track your past and current orders</span>
                </div>
                <div className="menu-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="menu-section">
              <h3 className="menu-section-title">Support</h3>
              <Link to="/support" className="menu-item">
                <div className="menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.9706 16.9706 21 12 21C10.4001 21 8.88837 20.6243 7.54753 19.9565L3 21L4.04346 16.4525C3.37568 15.1116 3 13.5999 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="menu-content">
                  <span className="menu-title">Help & Support</span>
                  <span className="menu-subtitle">Get help from our support team</span>
                </div>
                <div className="menu-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>

              <Link to="/privacy" className="menu-item">
                <div className="menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22S2 18 2 12V5L12 2L22 5V12C22 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="menu-content">
                  <span className="menu-title">Privacy Policy</span>
                  <span className="menu-subtitle">Learn about our privacy practices</span>
                </div>
                <div className="menu-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <BottomTabNav />
    </div>
  );
};

export default CustomerProfile;
